import React, { useCallback, useMemo, useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';

import { useExistingValues } from '@/shared/hooks/useExistingValues';
import { VALIDATION_MESSAGES } from '@/shared/lib/constants';
import { openTagInNewTab } from '@/shared/lib/navigation';
import { mapTagsToBasicInfo } from '@/shared/lib/tag';
import type { TagSource } from '@/shared/types/openmetadata';
import { BadgeList } from '@/shared/ui/BadgeList';
import type { TreeSelectNode } from '@/shared/ui/EditableTreeSelect';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { MetadataEditModal } from '@/shared/ui/MetadataEditModal';
import { Tooltip } from '@/shared/ui/Tooltip';

import { filterEntityLabels } from '@/entities/search';

import type { ColumnSchema } from '@/features/asset-detail/api/schemaApi';

import styles from './TagsCell.module.css';

export type TagsCellProps = {
  tags: ColumnSchema['tags'];
  columnName: string;
  tableFqn: string;
  tagsTreeData: TreeSelectNode[];
  updateColumnTags: (params: {
    tableFqn: string;
    columnName: string;
    tagFQNs: string[];
    source: TagSource;
  }) => Promise<void>;
  isUpdating?: boolean;
};

export const TagsCell: React.FC<TagsCellProps> = React.memo(
  ({ tags, columnName, tableFqn, tagsTreeData, updateColumnTags, isUpdating = false }) => {
    const classificationTags = useMemo(
      () =>
        filterEntityLabels(tags, {
          includeOnlySource: 'Classification',
          requireConfirmed: true,
        }),
      [tags]
    );

    const tagFQNs = useMemo(() => classificationTags.map((tag) => tag.tagFQN), [classificationTags]);

    const existingTagFQNs = useExistingValues(tagFQNs);

    const handleSaveTags = useCallback(
      async (newTagFQNs: string[]) => {
        await updateColumnTags({
          tableFqn,
          columnName,
          tagFQNs: newTagFQNs,
          source: 'Classification',
        });
      },
      [updateColumnTags, tableFqn, columnName]
    );

    const badgeItems = useMemo(() => mapTagsToBasicInfo(classificationTags), [classificationTags]);

    const renderTagDisplay = useCallback(() => {
      if (badgeItems.length === 0)
        return (
          <Tooltip title="태그 추가" placement="bottom">
            <div className={styles.addButtonContainer}>
              <PlusOutlined className={styles.addButtonIcon} />
              <span
                role="button"
                tabIndex={0}
                aria-label="태그 추가"
                className={styles.addButton}
                onClick={() => setIsEditing(true)}>
                추가
              </span>
            </div>
          </Tooltip>
        );
      return (
        <BadgeList
          isEllipsis={false}
          items={badgeItems}
          onItemClick={openTagInNewTab}
          existingValues={existingTagFQNs}
          maxVisibleCount={5}
        />
      );
    }, [badgeItems, existingTagFQNs]);

    const [isEditing, setIsEditing] = useState(false);

    const treeSelectValue = useMemo(() => {
      return classificationTags.map((tag) => ({
        value: tag.tagFQN,
        label: tag.displayName ?? tag.name,
      }));
    }, [classificationTags]);

    return (
      <div className={styles.tagsCell}>
        {renderTagDisplay()}
        {badgeItems.length > 0 && (
          <IconButton size="small" onClick={() => setIsEditing(true)} tooltip="태그 편집">
            <Icon name="edit" size={16} />
          </IconButton>
        )}
        <MetadataEditModal
          open={isEditing}
          icon={<Icon name="tag" size={12} />}
          title="태그 편집"
          firstLabel="선택된 태그"
          secondLabel="태그"
          searchValidation={{
            maxLength: 30,
            warningMessage: VALIDATION_MESSAGES.TAG_SEARCH_MAX_LENGTH,
          }}
          loading={isUpdating}
          treeSelectValue={treeSelectValue}
          treeData={tagsTreeData}
          onClose={() => setIsEditing(false)}
          onSubmit={handleSaveTags}
        />
      </div>
    );
  }
);

TagsCell.displayName = 'TagsCell';
